import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Container,
  Chip,
  Card,
  CardContent,
  Button,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { VideoTile } from './VideoTile';
import { fetchGroupVideos } from '../../services/channelService';
import { AUTH_ENDPOINTS } from '../../config/api';
import { useUserData } from '../../hooks/useUserData';
import { useUserSubscriptions } from '../../hooks/useUserSubscriptions';
import type { UserData, YouTubeVideo, YouTubeChannel, YouTubeSubscription } from '../../types/api.types';

export const Watch: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const {
    data: userData,
    error: userDataError,
    isLoading: isUserDataLoading,
    refetch: refetchUserData,
  } = useUserData();
  const {
    subscriptions,
    error: subscriptionsError,
    isLoading: isSubscriptionsLoading,
    refetch: refetchSubscriptions,
  } = useUserSubscriptions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [shorts, setShorts] = useState<YouTubeVideo[]>([]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Videos, 1 = Shorts
  const [channelMap, setChannelMap] = useState<Record<string, YouTubeChannel>>({});
  const [groupInfo, setGroupInfo] = useState<{
    name: string;
    channelCount: number;
    failedChannels: number;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showSnackbar = useCallback((message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const resetContent = useCallback(() => {
    setVideos([]);
    setShorts([]);
    setGroupInfo(null);
    setChannelMap({});
  }, []);

  const executeLoad = useCallback(
    async (resolvedUserData: UserData | null, resolvedSubscriptions: YouTubeSubscription[]) => {
      if (!groupId) {
        setError('Group ID is required');
        resetContent();
        setLoading(false);
        return;
      }

      if (!resolvedUserData) {
        setError('Authentication required. Please sign in.');
        resetContent();
        setLoading(false);
        return;
      }

      if (!resolvedUserData.profile?.email) {
        setError('Please connect your Google account first.');
        resetContent();
        setLoading(false);
        return;
      }

      const group = resolvedUserData.groups?.find(g => g.id === groupId);

      if (!group) {
        setError('Group not found.');
        resetContent();
        setLoading(false);
        return;
      }

      if (group.channelIds.length === 0) {
        setError(`Group "${group.name}" is empty.`);
        resetContent();
        setLoading(false);
        return;
      }

      const newChannelMap: Record<string, YouTubeChannel> = {};
      resolvedSubscriptions.forEach(sub => {
        newChannelMap[sub.channelId] = sub.channel;
      });
      setChannelMap(newChannelMap);

      try {
        setLoading(true);
        setError(null);

        const result = await fetchGroupVideos(groupId);

        setVideos(result.videos || []);
        setShorts(result.shorts || []);
        setGroupInfo({
          name: group.name,
          channelCount: group.channelIds.length,
          failedChannels: result.failedChannels,
        });

        if (result.failedChannels > 0) {
          showSnackbar(`Warning: ${result.failedChannels} of ${group.channelIds.length} channels failed to load.`);
        }
      } catch (err) {
        console.error('Error fetching watch data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        resetContent();
      } finally {
        setLoading(false);
      }
    },
    [groupId, resetContent, showSnackbar],
  );

  useEffect(() => {
    if (!groupId) {
      setError('Group ID is required');
      resetContent();
      setLoading(false);
      return;
    }

    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      setError('Authentication required. Please sign in.');
      resetContent();
      setLoading(false);
      return;
    }

    if (userDataError) {
      setError(userDataError.message);
      resetContent();
      setLoading(false);
      return;
    }

    if (subscriptionsError) {
      setError(subscriptionsError.message);
      resetContent();
      setLoading(false);
      return;
    }

    if (isUserDataLoading || isSubscriptionsLoading) {
      setLoading(true);
      return;
    }

    void executeLoad(userData, subscriptions);
  }, [
    executeLoad,
    groupId,
    isLoaded,
    isSignedIn,
    isSubscriptionsLoading,
    isUserDataLoading,
    resetContent,
    subscriptions,
    subscriptionsError,
    userData,
    userDataError,
  ]);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      const [userResult, subscriptionsResult] = await Promise.all([refetchUserData(), refetchSubscriptions()]);
      const refreshedUserData = userResult.data ?? userData ?? null;
      const refreshedSubscriptions = subscriptionsResult.data?.subscriptions ?? subscriptions;

      if (userResult.error) {
        throw userResult.error;
      }

      if (subscriptionsResult.error) {
        throw subscriptionsResult.error;
      }

      await executeLoad(refreshedUserData, refreshedSubscriptions);
    } catch (err) {
      console.error('Error refreshing watch data:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
      resetContent();
      setLoading(false);
    }
  }, [executeLoad, refetchSubscriptions, refetchUserData, resetContent, subscriptions, userData]);

  const handleBackToDashboard = () => {
    navigate('/app');
  };

  const handleConnectGoogle = () => {
    window.location.href = AUTH_ENDPOINTS.GOOGLE_LOGIN;
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading videos...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Fetching latest videos from your channels
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>

        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToDashboard}>
            Back to Dashboard
          </Button>

          {error.includes('connect') && (
            <Button variant="contained" color="primary" onClick={handleConnectGoogle}>
              Connect Google Account
            </Button>
          )}

          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="h4" component="h1">
            {groupInfo?.name}
          </Typography>
          <Chip label={`${groupInfo?.channelCount} channels`} variant="outlined" size="small" />
        </Box>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {/** TODO: only for premium members?? */}
            Refresh
          </Button>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {groupInfo?.failedChannels ? (
            <Chip
              label={`${groupInfo.failedChannels} failed to load`}
              variant="outlined"
              size="small"
              color="warning"
            />
          ) : null}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} aria-label="video type tabs">
          <Tab label={`Videos (${videos.length})`} />
          <Tab label={`Shorts (${shorts.length})`} />
        </Tabs>
      </Box>

      {/* Content based on active tab */}
      {activeTab === 0 ? (
        // Videos Tab
        videos.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                No videos found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                This group doesn't have any recent videos, or all channels failed to load.
              </Typography>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 3,
            }}
          >
            {videos.map(video => (
              <VideoTile key={`${video.channelId}-${video.id}`} video={video} channelMap={channelMap} />
            ))}
          </Box>
        )
      ) : // Shorts Tab
      shorts.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No shorts found
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This group doesn't have any recent shorts, or all channels failed to load.
            </Typography>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {shorts.map(video => (
            <VideoTile key={`${video.channelId}-${video.id}`} video={video} channelMap={channelMap} />
          ))}
        </Box>
      )}
    </Container>
  );
};
